/**
 * IEP API Service
 * Handles IEP, goals, and targets management
 */

import { get, post, put, del, upload } from './client';

// =============================================================================
// IEP Endpoints
// =============================================================================

/**
 * List IEPs with optional filtering
 * @param {Object} params - Query parameters (client_id, status, page, page_size)
 * @returns {Promise} Paginated list of IEPs
 */
export const listIeps = (params = {}) => get('/iep', params);

/**
 * Get active IEP for a client
 * @param {string} clientId - Client UUID
 * @returns {Promise} Active IEP data
 */
export const getActiveIep = (clientId) => get(`/iep/active/${clientId}`);

/**
 * Get IEP by ID
 * @param {string} iepId - IEP UUID
 * @returns {Promise} IEP data
 */
export const getIep = (iepId) => get(`/iep/${iepId}`);

/**
 * Create new IEP
 * @param {Object} data - IEP data
 * @returns {Promise} Created IEP
 */
export const createIep = (data) => post('/iep', data);

/**
 * Update IEP
 * @param {string} iepId - IEP UUID
 * @param {Object} data - IEP data to update
 * @returns {Promise} Updated IEP
 */
export const updateIep = (iepId, data) => put(`/iep/${iepId}`, data);

/**
 * Delete IEP
 * @param {string} iepId - IEP UUID
 * @returns {Promise} Delete response
 */
export const deleteIep = (iepId) => del(`/iep/${iepId}`);

/**
 * Approve IEP
 * @param {string} iepId - IEP UUID
 * @returns {Promise} Approved IEP
 */
export const approveIep = (iepId) => post(`/iep/${iepId}/approve`);

// =============================================================================
// IEP Version Endpoints (snapshot-based versioning)
// =============================================================================

/**
 * List every IEP version (snapshot) for a client, newest version first.
 * Pass includeSnapshot=true to also fetch the full form data per row (heavy).
 * @param {string} clientId
 * @param {{includeSnapshot?: boolean}} [opts]
 * @returns {Promise<Array>} Versions with id, iep_id, version_number, change_summary, created_at...
 */
export const listClientIepVersions = (clientId, { includeSnapshot = false } = {}) =>
  get(`/iep/clients/${clientId}/versions`, { include_snapshot: includeSnapshot });

/**
 * Create a new IEP version (snapshot) for a client. Backend auto-creates the
 * underlying client_iep row on first call. Each save always produces a new
 * version — no destructive edits.
 * @param {string} clientId
 * @param {{snapshot_data: object, change_summary?: string}} payload
 * @returns {Promise<object>} The created IEP version
 */
export const createClientIepVersion = (clientId, payload) => post(`/iep/clients/${clientId}/versions`, payload);

/**
 * Fetch a single IEP version with its full snapshot_data blob.
 * @param {string} versionId
 * @returns {Promise<object>}
 */
export const getIepVersion = (versionId) => get(`/iep/versions/${versionId}`);

/**
 * Update an existing IEP version in place — replaces snapshot_data and
 * (optionally) change_summary. Used by the in-place Edit flow so editing
 * does NOT spawn a new version row. New versions still come from
 * createClientIepVersion().
 * @param {string} versionId
 * @param {{snapshot_data: object, change_summary?: string}} payload
 * @returns {Promise<object>}
 */
export const updateIepVersion = (versionId, payload) => put(`/iep/versions/${versionId}`, payload);

// Goals + targets used to live in iep_version.targets_overlay. They now live
// in clinical.iep_goal / clinical.iep_target. The legacy helpers
// `getIepVersionTargets` and `replaceIepVersionGoalTargets` are removed —
// use listGoals / listTargets / createTarget / updateTarget / deleteTarget
// (defined further down) instead.

// =============================================================================
// Goal Endpoints
// =============================================================================

/**
 * List goals for an IEP
 * @param {string} iepId - IEP UUID
 * @param {Object} params - Query parameters
 * @returns {Promise} Paginated list of goals
 */
export const listGoals = (iepId, params = {}) => get(`/iep/${iepId}/goals`, params);

/**
 * Get goal by ID
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @returns {Promise} Goal data
 */
export const getGoal = (iepId, goalId) => get(`/iep/${iepId}/goals/${goalId}`);

/**
 * Create goal
 * @param {string} iepId - IEP UUID
 * @param {Object} data - Goal data
 * @returns {Promise} Created goal
 */
export const createGoal = (iepId, data) => post(`/iep/${iepId}/goals`, data);

/**
 * Update goal
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {Object} data - Goal data to update
 * @returns {Promise} Updated goal
 */
export const updateGoal = (iepId, goalId, data) => put(`/iep/${iepId}/goals/${goalId}`, data);

/**
 * Delete goal
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @returns {Promise} Delete response
 */
export const deleteGoal = (iepId, goalId) => del(`/iep/${iepId}/goals/${goalId}`);

/**
 * Approve a pending goal (admin acting as Clinical Head). Server enforces the
 * approver role — a supervisor cannot self-approve.
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {string} [note] - Optional note
 * @returns {Promise} Updated goal
 */
export const approveGoal = (iepId, goalId, note) => post(`/iep/${iepId}/goals/${goalId}/approve`, note ? { note } : {});

/**
 * Decline a pending goal (admin acting as Clinical Head).
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {string} [note] - Optional decline reason
 * @returns {Promise} Updated goal
 */
export const declineGoal = (iepId, goalId, note) => post(`/iep/${iepId}/goals/${goalId}/decline`, note ? { note } : {});

// =============================================================================
// Target Endpoints
// =============================================================================

/**
 * List targets for a goal
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {Object} params - Query parameters
 * @returns {Promise} Paginated list of targets
 */
export const listTargets = (iepId, goalId, params = {}) => get(`/iep/${iepId}/goals/${goalId}/targets`, params);

/**
 * Get target by ID
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {string} targetId - Target UUID
 * @returns {Promise} Target data
 */
export const getTarget = (iepId, goalId, targetId) => get(`/iep/${iepId}/goals/${goalId}/targets/${targetId}`);

/**
 * Create target
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {Object} data - Target data
 * @returns {Promise} Created target
 */
export const createTarget = (iepId, goalId, data) => post(`/iep/${iepId}/goals/${goalId}/targets`, data);

/**
 * Update target
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {string} targetId - Target UUID
 * @param {Object} data - Target data to update
 * @returns {Promise} Updated target
 */
export const updateTarget = (iepId, goalId, targetId, data) => put(`/iep/${iepId}/goals/${goalId}/targets/${targetId}`, data);

/**
 * Delete target
 * @param {string} iepId - IEP UUID
 * @param {string} goalId - Goal UUID
 * @param {string} targetId - Target UUID
 * @returns {Promise} Delete response
 */
export const deleteTarget = (iepId, goalId, targetId) => del(`/iep/${iepId}/goals/${goalId}/targets/${targetId}`);

// =============================================================================
// Convenience: Get all targets for a client (via active IEP)
// =============================================================================

/**
 * Fetch active IEP with all goals and targets in a single API call.
 * Returns { iep, goals: [{ ...goal, targets: [...] }] }
 * @param {string} clientId - Client UUID
 * @returns {Promise<Object>} IEP with goals and targets
 */
export const getClientActiveTargets = async (clientId) => {
  const iep = await get(`/iep/active/${clientId}/full`);
  if (!iep) {
    return { iep: null, goals: [] };
  }
  const goals = iep.goals || [];
  return { iep, goals };
};

// =============================================================================
// Mastery rule engine
// =============================================================================

/**
 * Get the org-wide mastery rule thresholds.
 * @returns {Promise} MasteryRuleConfig
 */
export const getMasteryRules = () => get('/iep/mastery-rules');

/**
 * Update the org-wide mastery rule thresholds.
 * @param {Object} data - partial thresholds
 * @returns {Promise} updated MasteryRuleConfig
 */
export const updateMasteryRules = (data) => put('/iep/mastery-rules', data);

/**
 * Run the mastery rule engine for a child's active IEP.
 * @param {string} clientId
 * @param {string} [sessionId]
 * @returns {Promise} { changes: [...], evaluated }
 */
export const executeRuleEngine = (clientId, sessionId = null) =>
  post('/iep/execute-rule-engine', { client_id: clientId, session_id: sessionId });

/**
 * A target's mastery status history, newest first: every change by the rule
 * engine or a person, with `details` explaining rule-engine decisions.
 * @param {string} targetId - iep_target id
 * @returns {Promise} { target_id, entries: [...] }
 */
export const getTargetStatusHistory = (targetId) => get(`/iep/targets/${targetId}/status-history`);

/**
 * Get a child's mastery-rule override state for their active IEP (plus the
 * global defaults used to seed the form).
 * @param {string} clientId
 * @returns {Promise} { enabled, scope, iep_id, global_rules, all_rules, goal_rules, target_rules }
 */
export const getChildMasteryRules = (clientId) => get(`/iep/child-mastery/${clientId}`);

/**
 * Turn the mastery rule engine on/off for one child (it also needs the global
 * switch on). Turning it back on does not recompute.
 * @returns {Promise} { engine_enabled, global_engine_enabled }
 */
export const setChildMasteryEngine = (clientId, enabled) => put(`/iep/child-mastery/${clientId}/engine`, { enabled });

/**
 * Save a child's mastery-rule override and re-run the rule engine.
 * @param {string} clientId
 * @param {Object} payload - { enabled, scope, all_rules, goal_rules, target_rules }
 * @returns {Promise} { saved, iep_id, engine }
 */
export const updateChildMasteryRules = (clientId, payload) => put(`/iep/child-mastery/${clientId}`, payload);

// =============================================================================
// IEP document import
// =============================================================================

/**
 * Reading an assessment report runs OCR (Document Intelligence) and then several
 * model calls on the server before it responds. Measured against two real 15-17 page
 * reports it takes 45-90s end to end, and a rate-limited deployment or a slow chunk
 * pushes that higher - well past the global axios timeout. The ceiling is sized for
 * the server's own worst case (ceil(chunks / concurrency) waves of the per-call
 * timeout, plus the OCR pass) so the client never aborts a request that is still
 * working; see reader.CALL_TIMEOUT_SECONDS in backend/app/services/iep_import.
 */
const IEP_IMPORT_TIMEOUT_MS = 300000;

/**
 * Read an ABA / IEP assessment report and return the IEP values it contains.
 *
 * EXTRACTION ONLY - the API saves nothing. The response is a proposed patch to the
 * IEP form's own state, plus the assessment grid images found in the document. The
 * supervisor reviews every section as a form and the form's ordinary Save is what
 * writes it down, so an import can never create or alter a child's IEP on its own.
 *
 * @param {File} file - the PDF
 * @param {Object} [opts]
 * @param {'document_intelligence'|'pdf_text'} [opts.prefer] - force an extractor
 * @returns {Promise} { patch, counts, needs_attention, images, warnings, provider, pages }
 */
export const importIepPdf = (file, { prefer } = {}) => {
  const body = new FormData();
  body.append('file', file);
  const query = prefer ? `?prefer=${encodeURIComponent(prefer)}` : '';
  return upload(`/client-lifecycle/iep/import${query}`, body, null, { timeout: IEP_IMPORT_TIMEOUT_MS });
};

export default {
  listIeps,
  getActiveIep,
  getIep,
  createIep,
  updateIep,
  deleteIep,
  approveIep,
  listGoals,
  getGoal,
  createGoal,
  updateGoal,
  deleteGoal,
  approveGoal,
  declineGoal,
  listTargets,
  getTarget,
  createTarget,
  updateTarget,
  deleteTarget,
  getClientActiveTargets,
  getMasteryRules,
  updateMasteryRules,
  executeRuleEngine,
  getTargetStatusHistory,
  getChildMasteryRules,
  updateChildMasteryRules,
  importIepPdf
};
